<template>
  <b-form>
    <b-form-group
      label-class="font-weight-bold"
      :label="$t('email')"
      label-for="email"
    >
      <b-form-input
        id="email"
        v-model="email"
        @input="email = email.toLowerCase()"
      ></b-form-input>
    </b-form-group>
    <b-form-group
      label-class="font-weight-bold"
      :label="$t('password')"
      label-for="password"
      class="password__input"
    >
      <b-input-group id="password">
        <b-form-input
          v-model="password"
          :type="passwordInputType"
          class="border-right-0"
        ></b-form-input>
        <b-input-group-append is-text>
          <b-icon
            :icon="passwordIcon"
            class="clickable"
            @click="changePasswordInputState"
          ></b-icon>
        </b-input-group-append>
        <b-input-group-append>
          <b-button
            variant="outline-secondary"
            class="input-append-btn"
            @click="passwordRevovery"
          >
            {{ $t("button.forgot") }}
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>
    <b-form-checkbox v-model="rememberMe">
      {{ $t("rememberMe") }}
    </b-form-checkbox>
    <!-- CAPTCHA (VULN-03) — only shown once the API asks for one -->
    <div v-if="isCaptchaRequired" class="mb-3">
      <div ref="recaptchaContainer"></div>
    </div>
    <b-row class="no-gutters  mt-5">
      <b-button
        block
        variant="red"
        :disabled="!isFormValid"
        @click="login"
        class="mb-1"
      >
        {{ $t("button.login") }}
      </b-button>
    </b-row>
  </b-form>
</template>

<script>
import Config from "../plugins/Config";
import { renderRecaptcha, getRecaptchaToken, resetRecaptcha } from "../utils/recaptcha";

export default {
  data() {
    return {
      email: "",
      password: "",
      showPassword: false,
      rememberMe: false
    };
  },
  computed: {
    isFormValid() {
      return this.email.length > 0 && this.password.length > 0;
    },
    passwordInputType() {
      return this.showPassword ? "text" : "password";
    },
    passwordIcon() {
      return this.showPassword ? "eye" : "eye-slash";
    },
    isCaptchaRequired() {
      return this.$store.getters["auth/isCaptchaRequired"];
    }
  },
  watch: {
    // VULN-03: render the CAPTCHA widget as soon as the store flags it required
    isCaptchaRequired(required) {
      if (!required) return;
      this.$nextTick(() => {
        const container = this.$refs.recaptchaContainer;
        if (!container) return;
        const siteKey = Config.value("recaptchaSiteKey");
        if (!siteKey) {
          console.error("VUE_APP_RECAPTCHA_SITE_KEY is not configured — cannot render CAPTCHA");
          return;
        }
        renderRecaptcha(container, siteKey).catch(err => {
          console.error("Failed to render reCAPTCHA:", err);
        });
      });
    }
  },
  methods: {
    login() {
      // capture BEFORE clearAll resets auth/captchaRequired (which hides the widget)
      const wasCaptchaRequired = this.isCaptchaRequired;
      const captchaToken =
        wasCaptchaRequired && this.$refs.recaptchaContainer
          ? getRecaptchaToken(this.$refs.recaptchaContainer)
          : undefined;

      if (wasCaptchaRequired && !captchaToken) {
        this.$store.commit(
          "error/SET_ERROR",
          {
            titleText: "dialog.error.loginTitle",
            messageText: "dialog.error.captchaRequiredMessage"
          },
          { root: true }
        );
        return;
      }

      this.$store.commit("loader/SET_LOADER", {});
      this.$store
        .dispatch("clearAll", null, { root: true })
        .then(() => {
          return this.$store.dispatch("auth/login", {
            email: this.email,
            password: this.password,
            captchaToken
          });
        })
        .catch(() => {
          console.log("error");
          if (this.$refs.recaptchaContainer) {
            resetRecaptcha(this.$refs.recaptchaContainer);
          }
        })
        .finally(() => this.$store.dispatch("loader/close"));
    },
    changePasswordInputState() {
      this.showPassword = !this.showPassword;
    },
    passwordRevovery() {
      this.$router.push({ name: "PasswordRecovery" });
    }
  }
};
</script>

<style scoped>
.input-append-btn {
  border: 1px solid #ced4da;
}
.password__input >>> .input-group-text {
  background-color: white;
  border-left: unset;
}
.password__input >>> input {
  min-width: 160px;
}
</style>
